import { z } from "zod";
import { FormBuilderFieldInputType } from "@/constants/form-builder";
import { type FormBuilderFormField } from "@/types/form-builder";
import type { TFunction } from "i18next";

export function createFieldValidation(
  field: FormBuilderFormField,
  t: TFunction,
): z.ZodTypeAny {
  if (field.inputType === FormBuilderFieldInputType.None) {
    return z.any().optional();
  }

  let fieldSchema: z.ZodTypeAny;
  const fieldLabel = field.showLabel ? field.label : "";
  const requiredError =
    field.requiredText || t("validation.error.code.REQUIRED");
  const atLeastOneError =
    field.requiredText || t("validation.error.code.AT_LEAST_ONE_OPTION");
  const invalidFieldError =
    field.validationText ||
    t("validation.error.code.INVALID_FIELD", {
      0: fieldLabel,
    });

  switch (field.inputType) {
    case FormBuilderFieldInputType.Text:
    case FormBuilderFieldInputType.Dropdown:
    case FormBuilderFieldInputType.Radio:
    case FormBuilderFieldInputType.CountryCodeDropdown:
    case FormBuilderFieldInputType.Date:
      fieldSchema = z.string({
        invalid_type_error: invalidFieldError,
      });
      break;

    case FormBuilderFieldInputType.Checkbox: {
      fieldSchema = z.array(z.string(), {
        invalid_type_error: invalidFieldError,
      });
      break;
    }

    case FormBuilderFieldInputType.SingleCheckbox: {
      fieldSchema = z.boolean({
        invalid_type_error: invalidFieldError,
      });
      break;
    }

    default:
      fieldSchema = z.any().optional();
  }

  if (field.required) {
    if (fieldSchema instanceof z.ZodString) {
      fieldSchema = fieldSchema.min(1, { message: requiredError });
    } else if (fieldSchema instanceof z.ZodArray) {
      fieldSchema = fieldSchema.min(1, { message: atLeastOneError });
    } else if (fieldSchema instanceof z.ZodNumber) {
      fieldSchema = fieldSchema.refine((val) => val !== undefined, {
        message: requiredError,
      });
    } else {
      fieldSchema = fieldSchema.refine(
        (val) => val !== undefined && val !== null,
        {
          message: requiredError,
        },
      );
    }
  } else {
    fieldSchema = fieldSchema.optional();
  }

  if (field.validationRegexp) {
    const regExp = new RegExp(field.validationRegexp);

    fieldSchema = fieldSchema.refine(
      (val) => val === undefined || val === "" || regExp.test(val),
      {
        message: invalidFieldError,
      },
    );
  }

  return fieldSchema;
}
